import React, { useEffect, useRef, useState } from "react";
import { FaHeart, FaPlus, FaTimes } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { IListItem } from "../../../../interfaces/IDocDetails";
import { formatDate } from "./helpers/formatDate";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateDocumentTag } from "./services/updateDocumentTag.service";
import { addFavoriteItem } from "./services/addFavoriteItem.service";
import { useFavoriteListContext } from "../../../../../../context/FavoriteListContext";
import { deleteFavoriteItem } from "./services/deleteFavoriteItem.service";
import { useOnClickOutside } from "../../../../../../hooks/useOnClickOutside";
import CreatableSelect from "react-select/creatable";
import { selectStyles } from "../../../../../AdminPanelPage/components/ManageUserTab/types/SelectStyles";

interface ListItemProps {
  item: IListItem;
  onTagsUpdate: (tags: string[]) => void;
  onRequestRemoveTag: (
    index: number,
    callback: (index: number) => void,
  ) => void;
  tagsData: string[];
}

const ListItem: React.FC<ListItemProps> = ({
  item,
  onTagsUpdate,
  onRequestRemoveTag,
  tagsData,
}) => {
  const { t } = useTranslation();
  const [isTruncated, setIsTruncated] = useState(true);
  const titleRef = useRef<HTMLSpanElement>(null);
  const [tags, setTags] = useState<string[]>(item.tags || []);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const { favoriteList, setFavoriteList } = useFavoriteListContext();
  const matchedFavoriteItem = favoriteList.find(
    (favItem) => favItem.case_no === item.case_no,
  );
  const [isFavourite, setIsFavourite] = useState(!!matchedFavoriteItem);
  const queryClient = useQueryClient();

  useEffect(() => {
    setIsFavourite(!!favoriteList.find((favItem) => favItem.case_no === item.case_no));
  }, [favoriteList, item.case_no]);
  const selectRef = useRef<HTMLDivElement>(null);
  const darkMode = localStorage.getItem("darkMode") === "true";

  useOnClickOutside(selectRef, () => {
    if (isAddingTag) {
      setIsAddingTag(false);
    }
  });

  const { mutate: updateTags } = useMutation({
    mutationFn: async (updatedTags: string[]) =>
      await updateDocumentTag(item.code, item.case_no, {
        tags: updatedTags,
      }),
    onError: (error: Error) => {
      console.error("Error updating document tags:", error);
    },
  });

  const { mutate: mutateCreateFavoriteItem } = useMutation({
    mutationFn: async () => await addFavoriteItem(item.code, item.case_no),
    onSuccess: (newFavoriteItem) => {
      setFavoriteList((prev) => [...prev, newFavoriteItem]);
      queryClient.invalidateQueries({ queryKey: ["favoriteListData"] });
    },
    onError: (error: Error) => {
      console.error("Error creating favorite item:", error);
    },
  });

  const { mutate: mutateDeleteFavoriteItem } = useMutation({
    mutationFn: async () => await deleteFavoriteItem(item.code, item.case_no),
    onSuccess: () => {
      setFavoriteList((prev) =>
        prev.filter((favItem) => favItem.case_no !== item.case_no),
      );
      queryClient.invalidateQueries({ queryKey: ["favoriteListData"] });
    },
    onError: (error: Error) => {
      console.error("Error deleting favorite item:", error);
    },
  });

  useEffect(() => {
    if (titleRef.current) {
      setIsTruncated(
        titleRef.current.scrollWidth > titleRef.current.clientWidth,
      );
    }
  }, [item.title]);

  const handleRemoveTag = (index: number) => {
    const updatedTags = tags.filter((_, i) => i !== index);
    setTags(updatedTags);
    onTagsUpdate(updatedTags);

    updateTags(updatedTags);
  };

  const handleAddFavorite = () => {
    if (isFavourite) {
      mutateDeleteFavoriteItem();
      setIsFavourite(false);
    } else {
      mutateCreateFavoriteItem();
      setIsFavourite(true);
    }
  };

  const tagOptions: any = Array.from(new Set(tagsData))
    .filter(Boolean)
    .map((tag) => ({ value: tag, label: tag }));

  return (
    <div className="px-3 pt-3 pb-4 bg-base-color rounded-lg border border-slate-300 dark:border-slate-700 transition duration-300">
      <div className="text-lg text-del-green dark:text-gray-300 font-bold flex justify-between">
        <span
          ref={titleRef}
          onClick={() =>
            window.open(`/details/${item.code}/${item.case_no}`, "_blank")
          }
          className={`hover:underline cursor-pointer text-xl dark:text-del-green ${
            isTruncated ? `tooltip-${item.id}` : ""
          } truncate overflow-hidden text-ellipsis whitespace-nowrap tooltip-target`}
        >
          {item.title}
        </span>
        <div className="flex items-center justify-end space-x-2">
          <button type="button" onClick={(e) => { e.stopPropagation(); handleAddFavorite(); }}>
            <FaHeart
              className={
                isFavourite
                  ? "text-del-light-theme-green text-xl dark:text-del-green"
                  : "text-gray-400 text-xl hover-btn-del-green dark:text-gray-500"
              }
            />
          </button>
        </div>
        {isTruncated && (
          <Tooltip
            anchorSelect={`.tooltip-${item.id}`}
            place="top"
            positionStrategy="fixed"
            className="text-xs font-light z-50"
            content={item.title}
            style={{ maxWidth: "100vh", whiteSpace: "normal" }}
          />
        )}
      </div>
      {item.gazette_number && (
        <div className="text-sm text-secondary-color">
          <span className="font-semibold">{t("docInfo.gazetteNumber")} </span>
          {item.gazette_number}
        </div>
      )}
      <div className="text-sm text-secondary-color">
        <span className="font-semibold">
          {item.code.includes("calendar")
            ? t("docInfo.dateOfHearing")
            : t("docInfo.dateOfPublication")}{" "}
        </span>
        {formatDate(item.date)}
      </div>
      <div className="text-sm text-secondary-color">
        <span className="font-semibold">{t("docInfo.regulators")} </span>
        {!!item.regulator ? item.regulator : item.code}
      </div>
      <div className="text-sm text-secondary-color">
        <span className="font-semibold">{t("docInfo.class")} </span>
        {item.classes.join(", ")}
      </div>
      {item.tax_alert && (
        <div className="text-sm text-secondary-color">
          <span className="font-semibold">{t("docInfo.taxAlert")} </span>
          {item?.tax_alert.join(", ")}
        </div>
      )}
      {item.signature && (
        <div className="text-sm text-secondary-color">
          <span className="font-semibold">{t("docDetails.signature")}: </span>
          {item.signature}
        </div>
      )}
      {item.city && (
        <div className="text-sm text-secondary-color">
          <span className="font-semibold">{t("docInfo.city")} </span>
          {item.city}
        </div>
      )}
      {item.judges && (
        <div className="text-sm text-secondary-color">
          <span className="font-semibold">{t("docInfo.judges")} </span>
          {item.judges.join(", ")}
        </div>
      )}
      {item.status && (
        <div className="text-sm text-secondary-color">
          <span className="font-semibold">{t("docInfo.status")} </span>
          {item.status}
        </div>
      )}
      {item.tech_product && (
        <div className="text-sm text-secondary-color">
          <span className="font-semibold">{t("docInfo.techProduct")} </span>
          {item.tech_product}
        </div>
      )}
      {item.tech_section_vat_act && (
        <div className="text-sm text-secondary-color">
          <span className="font-semibold">
            {t("docInfo.techSectionVATAct")}{" "}
          </span>
          {item.tech_section_vat_act.join(", ")}
        </div>
      )}
      {item.short_summary && (
        <div className="text-sm text-secondary-color">
          <span className="font-bold">{t("docInfo.shortSummary")} </span>
          {item.short_summary !== "" ? item.short_summary : "-"}
        </div>
      )}
      <div className="flex flex-wrap items-center">
        {item?.keywords?.map(
          (keyword, index) =>
            keyword !== "" && (
              <span
                key={index}
                className="inline-flex items-center justify-center keyword-chip-color mt-1 mr-1"
              >
                {keyword}
              </span>
            ),
        )}
        {tags?.map((tag, index) => (
          <span
            key={index}
            className="inline-flex items-center justify-center tag-chip-color mt-1 mr-1"
          >
            {tag}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRequestRemoveTag(index, handleRemoveTag); }}
              className="ml-1 mt-0.5"
            >
              <FaTimes className={`tooltip-remove-btn-${item.id} text-white`} />
            </button>
          </span>
        ))}
        <Tooltip
          anchorSelect={`.tooltip-remove-btn-${item.id}`}
          place="bottom"
          positionStrategy="fixed"
          className="text-xs font-light z-50"
          content={t("tooltips.remove")}
        />
        {isAddingTag ? (
          <div ref={selectRef} className="min-w-[150px] max-w-[250px]">
            <CreatableSelect<{ value: string; label: string }>
              value={null}
              onChange={(opt) => {
                if (!opt || typeof opt.value !== "string") {
                  setIsAddingTag(false);
                  return;
                }
                const chosen = opt.value.trim();
                if (!chosen || tags.includes(chosen)) {
                  setIsAddingTag(false);
                  return;
                }
                const updated = [...tags, chosen];
                setTags(updated);
                onTagsUpdate(updated);
                updateTags(updated);
                setIsAddingTag(false);
              }}
              options={tagOptions}
              isClearable
              placeholder={t("filtersSection.searchAndSelectTags")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                ...selectStyles(darkMode),
                control: (base: any, state: any) => ({
                  ...base,
                  minHeight: "23px",
                  height: "26px",
                  marginTop: "4px",
                  paddingBottom: "10px",
                  fontSize: "14px",
                  backgroundColor: darkMode ? "#4B5563" : "#ffffff",
                  color: darkMode ? "#fff" : "#000",
                  borderColor: darkMode ? "#374151" : "#9CA3AF",
                  minWidth: "100px",
                  boxShadow: state.isFocused
                    ? `0 0 0 2px ${darkMode ? "#86BC25" : "#86BC25"}`
                    : base.boxShadow,
                  borderWidth: "1px",
                  outline: "none",
                  transition: "box-shadow 0.2s, border-color 0.2s",
                }),
                placeholder: (base) => ({
                  ...base,
                  paddingBottom: "5px",
                  fontSize: "14px",
                  color: darkMode ? "#fff" : "#000",
                }),
                valueContainer: (base) => ({
                  ...base,
                  paddingTop: 0,
                  paddingBottom: 0,
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  height: "28px",
                }),
                indicatorSeparator: (base) => ({
                  ...base,
                  marginBottom: "10px",
                  marginTop: "5px",
                }),
                dropdownIndicator: (base) => ({
                  ...base,
                  padding: "0 0 0 5px",
                  marginBottom: "5px",
                }),
                input: (base) => ({
                  ...base,
                  color: darkMode ? "#FFFFFF" : "#000000",
                }),
                option: (base, { isFocused }) => ({
                  ...base,
                  backgroundColor: isFocused
                    ? darkMode
                      ? "#86BC25"
                      : "#86BC25"
                    : darkMode
                      ? "#374151"
                      : "#FFFFFF",
                  color: darkMode ? "#FFFFFF" : "#000000",
                  fontSize: "14px",
                }),
              }}
              formatCreateLabel={(v) => `Create "${v}"`}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setIsAddingTag(true); }}
            className={`mt-1 tooltip-btn-${item.id} text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white`}
          >
            <FaPlus />
          </button>
        )}
        <Tooltip
          anchorSelect={`.tooltip-btn-${item.id}`}
          place="bottom"
          positionStrategy="fixed"
          className="text-xs font-light z-50"
          content={t("tooltips.addTag")}
        />
      </div>

      {item.highlights && (
        <>
          <hr className="my-1 border-gray-300 dark:border-gray-600" />
          <div className="text-sm text-base-color">{item.highlights}</div>
        </>
      )}
    </div>
  );
};

export { ListItem };
